import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BadgeCheck, Mail, CheckCircle2, Gift, Users } from "lucide-react";

import { useTranslation } from "react-i18next";
export default function Register() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { register, formatApiError } = useAuth();
  const [inviteCode, setInviteCode] = useState(params.get("invite") || params.get("code") || "");
  // Brick 6 N16 — capture ?ref=<code> so we can forward it to /auth/register.
  // Read once on mount so it survives the invite-preview form churn.
  const [referralCode] = useState((params.get("ref") || "").trim().toUpperCase());
  // Public preview of the referral chip — shows the referring pump's name.
  const [referralPreview, setReferralPreview] = useState(null);
  const [referralInvalid, setReferralInvalid] = useState(false);
  const [invite, setInvite] = useState(null); // populated when code is valid
  const [inviteError, setInviteError] = useState(null);
  const [checkingInvite, setCheckingInvite] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [pumpName, setPumpName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [omc, setOmc] = useState("OTHER");
  const [omcList, setOmcList] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/reference/omc-list").then((r) => setOmcList(r.data.omc || [])).catch(() => {});
  }, []);

  // Auto-fetch referral preview when ?ref= arrives on URL. Silent 404 if code
  // is stale/invalid — we just hide the chip and drop the referral_code so
  // signup doesn't fail. Never blocks the form.
  useEffect(() => {
    if (!referralCode) return;
    let dead = false;
    api.get(`/referrals/${encodeURIComponent(referralCode)}/preview`)
      .then((r) => !dead && setReferralPreview(r.data))
      .catch(() => !dead && setReferralInvalid(true));
    return () => { dead = true; };
  }, [referralCode]);

  // Auto-validate an invite code from URL on mount
  useEffect(() => {
    if (inviteCode) checkInvite(inviteCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkInvite = async (code) => {
    const c = (code || "").trim().toUpperCase();
    if (!c) return;
    setCheckingInvite(true); setInviteError(null); setInvite(null);
    try {
      const { data } = await api.get(`/dealer-invites/${c}/preview`);
      setInvite(data);
      setPumpName(data.pump_name || "");
      setCompanyName(data.company_name || "");
      setOmc(data.omc || "OTHER");
      // Nudge dealer to use the invite-associated email as their contact
      toast.success(`Invite verified — welcome, ${data.dealer_name}!`);
    } catch (e) {
      setInviteError(e?.response?.data?.detail || "Invalid or expired invite code");
    } finally {
      setCheckingInvite(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!invite) return toast.error(t("auth.register.enter_your_invite_code_first"));
    setSubmitting(true);
    try {
      await register({
        name, phone: phone.trim(), password, pump_name: pumpName,
        omc, company_name: companyName || null,
        invite_code: inviteCode.trim().toUpperCase(),
        referral_code: referralPreview?.valid ? referralCode : null,
      });
      toast.success("Account created — let's set up your pump");
      nav("/setup", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-8">
      <div className="w-full max-w-md">
        {/* Brick 6 N16 — Referral chip: shows the referrer's pump name so the
            new dealer knows their signup credits a real person. Silent when
            no ?ref= is on the URL, or the code is invalid. */}
        {referralPreview?.valid && (
          <div
            className="mb-3 flex items-center gap-3 rounded-2xl border-2 border-sky-500/40 bg-gradient-to-br from-sky-500/10 to-sky-500/5 p-3 shadow-sm"
            data-testid="register-referral-chip"
          >
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-sky-500 text-white">
              <Users className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-black uppercase tracking-widest text-sky-700 dark:text-sky-300">
                Referred by a friend
              </div>
              <div className="text-sm font-bold text-foreground">
                <span className="text-sky-700 dark:text-sky-300">{referralPreview.referrer_pump_name}</span> invited you
              </div>
              <div className="text-[11px] text-muted-foreground">
                Both of you get 2 free months once HisabX verifies your pump.
              </div>
            </div>
          </div>
        )}
        {referralInvalid && referralCode && (
          <div
            className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-200"
            data-testid="register-referral-invalid"
          >
            Referral code <b>{referralCode}</b> isn&apos;t valid — signup will continue without it.
          </div>
        )}

        {/* 3-Month Free Trial banner */}
        <div className="mb-4 flex items-center gap-3 rounded-2xl border-2 border-emerald-500/40 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 p-3 shadow-sm" data-testid="register-trial-banner">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500 text-white">
            <Gift className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300">{t("auth.register.welcome_gift")}</div>
            <div className="text-sm font-bold text-foreground">First 6 months free · No credit card</div>
          </div>
        </div>

        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-lg bg-amber-500 text-white">
            <BadgeCheck className="h-7 w-7" />
          </div>
          <div className="text-2xl font-bold text-foreground">{t("auth.register.create_dealer_account")}</div>
          <div className="text-xs text-muted-foreground">{t("auth.register.invite_only_use_the_code_from_your_hisab")}</div>
        </div>

        {/* Google shortcut removed — invite-only signup is now phone+code based */}

        {/* Invite gate */}
        <Card className="mb-4 border-border">
          <CardHeader>
            <CardTitle className="text-base">1. Invite code</CardTitle>
          </CardHeader>
          <CardContent>
            {invite ? (
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid="invite-verified">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 text-emerald-600" />
                  <div className="min-w-0 text-xs">
                    <div className="font-semibold text-emerald-900">{t("auth.register.invite_verified")}</div>
                    <div className="text-emerald-800">
                      For <b>{invite.dealer_name}</b> · {invite.company_name || invite.pump_name}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1 text-emerald-700"><Mail className="h-3 w-3" /> {invite.email}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => { setInvite(null); setInviteCode(""); }} data-testid="invite-clear">
                    Change
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  placeholder={t("auth.register.enter_code_e_g_dlr_xxxxxxxx")}
                  value={inviteCode}
                  onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); setInviteError(null); }}
                  data-testid="invite-code-input"
                  className="h-11 md:h-9"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => checkInvite(inviteCode)}
                  disabled={!inviteCode || checkingInvite}
                  className="w-full h-11 md:h-9"
                  data-testid="invite-code-verify"
                >
                  {checkingInvite ? "Checking…" : "Verify invite"}
                </Button>
                {inviteError && (
                  <div className="rounded bg-red-50 p-2 text-xs text-red-700" data-testid="invite-error">{inviteError}</div>
                )}
                <p className="pt-1 text-[11px] text-muted-foreground">
                  Don't have a code? Reach out at <b>hello@hisabx.in</b> to purchase HisabX for your pump.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Sign-up form (disabled until invite verified) */}
        <Card className={"border-border " + (invite ? "" : "opacity-60")}>
          <CardHeader>
            <CardTitle className="text-base">2. Your details</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <fieldset disabled={!invite} className="space-y-4">
                <div>
                  <Label htmlFor="rname">{t("auth.register.your_full_name")}</Label>
                  <Input id="rname" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" className="h-11 md:h-9" required />
                </div>
                <div>
                  <Label htmlFor="rphone">{t("auth.register.phone_10_digit")}</Label>
                  <Input id="rphone" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} data-testid="register-phone" className="h-11 md:h-9" required />
                </div>
                <div>
                  <Label htmlFor="rpw">{t("auth.register.password_min_6_chars")}</Label>
                  <Input id="rpw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" className="h-11 md:h-9" required minLength={6} />
                </div>
                <div>
                  <Label htmlFor="rpump">{t("auth.register.pump_outlet_name")}</Label>
                  <Input id="rpump" value={pumpName} onChange={(e) => setPumpName(e.target.value)} placeholder="e.g. Sunshine Fuels — Sector 5" data-testid="register-pump-name" className="h-11 md:h-9" required />
                </div>
                <div>
                  <Label htmlFor="rcompany">{t("auth.register.company_firm_name_optional")}</Label>
                  <Input id="rcompany" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Sunshine Petro Retail Pvt Ltd" data-testid="register-company-name" className="h-11 md:h-9" />
                </div>
                <div>
                  <Label>{t("auth.register.omc")}</Label>
                  <Select value={omc} onValueChange={setOmc}>
                    <SelectTrigger data-testid="register-omc" className="h-11 md:h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {omcList.map((o) => (
                        <SelectItem key={o.code} value={o.code}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="submit" className="h-11 w-full md:h-10" disabled={submitting || !invite} data-testid="register-submit">
                  {submitting ? "Creating account..." : "Create account"}
                </Button>
              </fieldset>
            </form>
            <div className="mt-4 text-center text-xs text-muted-foreground">
              Already have an account? <Link to="/login" className="font-semibold text-amber-600 hover:underline">{t("auth.register.sign_in")}</Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
